import type { ValidatorMessage } from 'helpers/validators';
import type { FieldProps } from 'informed';
import { asField } from 'informed';
import type React from 'react';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { CustomFormFieldProps } from './contracts';
import { ProfileSelectBlock } from './primitives/ProfileSelects/ProfileSelectBlock';
import type { SelectBlockProps } from './primitives/SelectBlock';
import { SelectBlock } from './primitives/SelectBlock';
import { getErrorTranslation } from './utils';

function asSelectField<P extends SelectBlockProps, V>(
  Component: React.ComponentType<P>
): React.ComponentType<FieldProps<V, any> & P & CustomFormFieldProps> {
  return asField(
    ({
      fieldState,
      fieldApi,
      forwardedRef,
      render,
      field,
      translateError = true,
      onSelect,
      selected,
      onClose,
      isFilter,
      error: propsError,
      ...rest
    }) => {
      const { t } = useTranslation();

      const { setValue, setTouched } = fieldApi;
      const { value, error } = fieldState;

      useEffect(() => {
        isFilter && setValue(selected as any);
      }, [selected, setValue, isFilter]);

      const handleSelect = useCallback(
        (value: V) => {
          setValue(value);
          setTouched(true);

          if (onSelect) {
            onSelect(value as any);
          }
        },
        [setValue, setTouched, onSelect]
      );

      const handleClose = useCallback(() => {
        setTouched(true);

        if (onClose) {
          onClose();
        }
      }, [setTouched, onClose]);

      const getError = useCallback(() => {
        if (propsError != null) {
          return propsError;
        }

        if (error != null && translateError) {
          return getErrorTranslation(t, error as ValidatorMessage);
        }
        return error;
      }, [t, propsError, error, translateError]);

      return (
        // FIXME: typescript error
        // @ts-ignore
        <Component
          {...rest}
          error={getError()}
          onClose={handleClose}
          onSelect={handleSelect}
          ref={forwardedRef}
          selected={value}
        />
      );
    }
  );
}

export const Select = asSelectField(SelectBlock);

export const ProfileSelect = asSelectField(ProfileSelectBlock);
